import React, { useEffect, useState } from 'react';
import { RocketLaunch, CheckCircle, Circle, CaretRight, X, Play } from '@phosphor-icons/react';
import { getTourProgress, listTourSteps } from '../services/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';

/**
 * Barre de progression compacte + panneau détaillé au clic.
 * - Clic sur la barre → ouvre un panneau latéral listant toutes les étapes
 * - Chaque étape non-terminée peut être "sautée directement" (relance le tour à cet index)
 * - Se masque automatiquement si le tour est terminé ou vide
 */
export function TourProgressBar({ collapsed = false }) {
  const [data, setData] = useState({ completed_count: 0, total_steps: 0, completed_ids: [], tour_completed: false });
  const [steps, setSteps] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const refresh = () => {
    getTourProgress()
      .then((r) => setData(r.data || {}))
      .catch(() => setHidden(true));
    listTourSteps()
      .then((r) => setSteps(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  if (hidden) return null;
  if (!data.total_steps) return null;
  if (data.tour_completed && data.completed_count === data.total_steps) return null;

  const pct = data.total_steps > 0 ? Math.round((data.completed_count / data.total_steps) * 100) : 0;
  const completedSet = new Set(data.completed_ids || []);

  const jumpToStep = (idx) => {
    try { localStorage.removeItem('oscop_tour_seen_v1'); } catch {}
    setPanelOpen(false);
    requestAnimationFrame(() => window.dispatchEvent(new CustomEvent('oscop:tour:open', { detail: { index: idx } })));
  };

  const handleOpen = () => {
    refresh();
    setPanelOpen(true);
  };

  const trigger = collapsed ? (
    <button
      onClick={handleOpen}
      className="w-full px-2 py-2 border-t border-white/10 hover:bg-white/5 transition-colors"
      data-testid="tour-progress-collapsed"
      title={`Onboarding : ${data.completed_count}/${data.total_steps}`}
    >
      <div className="relative w-8 h-8 mx-auto">
        <svg viewBox="0 0 36 36" className="w-8 h-8">
          <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
          <circle
            cx="18" cy="18" r="16" fill="none"
            stroke="#80C51F" strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * 100.5} 100.5`}
            transform="rotate(-90 18 18)"
            style={{ transition: 'stroke-dasharray 0.6s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <RocketLaunch size={12} className="text-[#80C51F]" weight="fill" />
        </div>
      </div>
    </button>
  ) : (
    <button
      onClick={handleOpen}
      className="w-full px-3 py-2.5 mx-2 mb-2 rounded-lg bg-gradient-to-br from-[#2D5A4C]/40 to-[#00AEEF]/25 border border-white/10 hover:border-white/30 hover:from-[#2D5A4C]/60 hover:to-[#00AEEF]/40 transition-all text-left group"
      data-testid="tour-progress-bar"
      style={{ width: 'calc(100% - 16px)' }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/80 font-bold">
          <RocketLaunch size={11} weight="fill" className="text-[#80C51F]" />
          Onboarding
        </div>
        <span className="text-[10px] text-white/90 font-mono font-bold" data-testid="tour-progress-count">
          {data.completed_count}/{data.total_steps}
        </span>
      </div>
      <div className="h-1.5 bg-white/15 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#80C51F] to-[#00AEEF] rounded-full transition-all duration-700"
          style={{ width: `${pct}%` }}
          data-testid="tour-progress-fill"
        />
      </div>
      <p className="text-[9px] text-white/60 mt-1 flex items-center gap-1 group-hover:text-white/90 transition-colors">
        {data.completed_count === data.total_steps ? (
          <><CheckCircle size={9} weight="fill" className="text-[#80C51F]" /> Prêt à terminer le tour</>
        ) : (
          <>Cliquer pour voir le détail <CaretRight size={9} weight="bold" /></>
        )}
      </p>
    </button>
  );

  return (
    <>
      {trigger}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="tour-progress-panel">
          <SheetHeader className="p-6 pb-4 bg-gradient-to-br from-[#2D5A4C] to-[#1E4136] text-white">
            <SheetTitle className="text-white flex items-center gap-2">
              <RocketLaunch size={20} weight="fill" className="text-[#80C51F]" />
              Progression du tour d'accueil
            </SheetTitle>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1 h-2 bg-white/15 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#80C51F] to-[#00AEEF] rounded-full transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-sm font-mono font-bold whitespace-nowrap">{data.completed_count}/{data.total_steps} · {pct}%</span>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#F9F8F5]" data-testid="tour-steps-list">
            {steps.length === 0 && (
              <p className="text-center text-[#5E6D66] text-sm py-8">Aucune étape configurée.</p>
            )}
            {steps.map((s, idx) => {
              const done = completedSet.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`p-3 rounded-lg border transition-all ${done ? 'bg-emerald-50/60 border-emerald-200' : 'bg-white border-[#E8E6E1] hover:border-[#2D5A4C]/40'}`}
                  data-testid={`tour-step-item-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 mt-0.5 ${done ? 'text-emerald-600' : 'text-[#5E6D66]'}`}>
                      {done ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} weight="regular" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${done ? 'text-emerald-900 line-through decoration-emerald-400/50' : 'text-[#1A2421]'}`}>
                        <span className="opacity-60 mr-1.5 font-mono text-xs">{String(idx + 1).padStart(2, '0')}</span>
                        {s.title}
                      </p>
                      <p className="text-[11px] text-[#5E6D66] mt-1 line-clamp-2">{s.body}</p>
                      {!done && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => jumpToStep(idx)}
                          className="mt-2 h-7 text-[11px] border-[#2D5A4C]/30 text-[#2D5A4C] hover:bg-[#2D5A4C] hover:text-white"
                          data-testid={`tour-step-jump-${idx}`}
                        >
                          <Play size={10} className="mr-1" weight="fill" />
                          Sauter à cette étape
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-4 border-t border-[#E8E6E1] bg-white flex items-center justify-between text-[11px] text-[#5E6D66]">
            <span>Astuce : cliquez sur une étape non terminée pour la relancer.</span>
            <Button size="sm" variant="ghost" onClick={() => setPanelOpen(false)}>
              <X size={12} className="mr-1" /> Fermer
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default TourProgressBar;
